import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { baseUrlApi } from '../../utils/url';

const getHeaders = () => {
  try {
    const raw = localStorage.getItem('user');
    const token = raw ? JSON.parse(raw).token : null;
    return token ? { Authorization: `bearer ${token}` } : {};
  } catch { return {}; }
};

export const fetchLeaseKeys = createAsyncThunk(
  'leaseKey/fetchByLease',
  async (leaseId: number) => {
    const [keys, photos] = await Promise.all([
      axios.get(`${baseUrlApi}/leasekeys/lease/${leaseId}`, { headers: getHeaders() }),
      axios.get(`${baseUrlApi}/leasekeys/lease/${leaseId}/photos`, { headers: getHeaders() }),
    ]);
    return { keys: keys.data, photos: photos.data };
  }
);

export const upsertLeaseKey = createAsyncThunk(
  'leaseKey/upsert',
  async ({ leaseId, data }: { leaseId: number; data: any }) => {
    const res = await axios.put(`${baseUrlApi}/leasekeys/lease/${leaseId}`, data, { headers: getHeaders() });
    return res.data;
  }
);

export const deleteLeaseKey = createAsyncThunk(
  'leaseKey/delete',
  async (id: number) => {
    await axios.delete(`${baseUrlApi}/leasekeys/${id}`, { headers: getHeaders() });
    return id;
  }
);

export const uploadLeaseKeyPhoto = createAsyncThunk(
  'leaseKey/uploadPhoto',
  async ({ leaseId, file }: { leaseId: number; file: File }) => {
    const fd = new FormData();
    fd.append('photo', file);
    const res = await axios.post(`${baseUrlApi}/leasekeys/lease/${leaseId}/photos`, fd, { headers: getHeaders() });
    return res.data;
  }
);

export const deleteLeaseKeyPhoto = createAsyncThunk(
  'leaseKey/deletePhoto',
  async (id: number) => {
    await axios.delete(`${baseUrlApi}/leasekeys/photos/${id}`, { headers: getHeaders() });
    return id;
  }
);

interface LeaseKeyState {
  keys:    any[];
  photos:  any[];
  loading: boolean;
}

const leaseKeySlice = createSlice({
  name: 'leaseKey',
  initialState: { keys: [], photos: [], loading: false } as LeaseKeyState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchLeaseKeys.pending,  state => { state.loading = true; })
      .addCase(fetchLeaseKeys.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.keys    = payload.keys;
        state.photos  = payload.photos;
      })
      .addCase(fetchLeaseKeys.rejected, state => { state.loading = false; })
      .addCase(upsertLeaseKey.fulfilled, (state, { payload }) => {
        const idx = state.keys.findIndex(k => k.id === payload.id);
        if (idx >= 0) state.keys[idx] = payload;
        else state.keys.push(payload);
      })
      .addCase(deleteLeaseKey.fulfilled, (state, { payload }) => {
        state.keys = state.keys.filter(k => k.id !== payload);
      })
      .addCase(uploadLeaseKeyPhoto.fulfilled, (state, { payload }) => {
        state.photos.push(payload);
      })
      .addCase(deleteLeaseKeyPhoto.fulfilled, (state, { payload }) => {
        state.photos = state.photos.filter(p => p.id !== payload);
      });
  },
});

export default leaseKeySlice.reducer;
